import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { usePublicSettings } from "@/lib/settings";
import {
  Banknote, Copy, CreditCard, FileText, ShieldCheck, Wallet, Receipt,
  Upload, ArrowRight, MapPin,
} from "lucide-react";
import { toast } from "sonner";

const Step = ({ n, title, children }) => (
  <article className="surface-card p-5 sm:p-6" data-testid={`pay-step-${n}`}>
    <div className="flex gap-4">
      <span className="font-display uppercase text-spark-teal text-2xl font-bold shrink-0 leading-none">{n.toString().padStart(2, "0")}</span>
      <div>
        <h3 className="font-display uppercase tracking-tight text-lg sm:text-xl font-bold mb-1.5">{title}</h3>
        <div className="text-spark-slate text-sm leading-relaxed space-y-2">{children}</div>
      </div>
    </div>
  </article>
);

const HowToPay = () => {
  const settings = usePublicSettings();
  const [pay, setPay] = useState(null);

  useEffect(() => {
    api.get("/settings/payment").then((res) => setPay(res.data)).catch(() => setPay({}));
  }, []);

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const gstPct = pay?.gst_rate ? Math.round(pay.gst_rate * 100) : 15;

  return (
    <div className="min-h-screen bg-spark-floor" data-testid="how-to-pay-page">
      <Navbar />

      <div className="bg-spark-teal-deep text-white border-b-4 border-spark-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <p className="label-mono text-spark-yellow mb-2">Help · Payments &amp; receipts</p>
          <h1 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95]">How to pay</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Bank transfer is our preferred method — fast, free, and your payment lands directly with the workshop. Below is everything you need to pay your invoice and get a tax receipt.</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 space-y-12">

        {/* Bank details quick-card */}
        <section data-testid="pay-bank-card">
          <div className="surface-card p-6 sm:p-8">
            <p className="label-mono text-spark-teal flex items-center gap-2"><Banknote className="size-3.5" /> Bank transfer (preferred)</p>
            <p className="text-spark-slate text-sm mt-2">Most customers use online banking — Internet, mobile app, or in person at any branch.</p>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {pay?.bank_account_name && (
                <li>
                  <p className="text-xs uppercase tracking-wider text-spark-slate">Account name</p>
                  <p className="font-semibold text-spark-ink mt-0.5">{pay.bank_account_name}</p>
                </li>
              )}
              {pay?.bank_name && (
                <li>
                  <p className="text-xs uppercase tracking-wider text-spark-slate">Bank</p>
                  <p className="font-semibold text-spark-ink mt-0.5">{pay.bank_name}</p>
                </li>
              )}
              {pay?.bank_account_number && (
                <li className="sm:col-span-2">
                  <p className="text-xs uppercase tracking-wider text-spark-slate">Account number</p>
                  <p className="font-mono font-bold text-spark-ink mt-0.5 flex items-center gap-2 text-base">
                    {pay.bank_account_number}
                    <button onClick={() => copy(pay.bank_account_number)} className="text-spark-teal hover:text-spark-teal-deep" aria-label="Copy account number" data-testid="copy-bank-number">
                      <Copy className="size-4" />
                    </button>
                  </p>
                </li>
              )}
              <li className="sm:col-span-2">
                <p className="text-xs uppercase tracking-wider text-spark-slate">Reference (very important)</p>
                <p className="font-semibold text-spark-ink mt-0.5">Use your <span className="font-mono text-spark-teal">SPK-XX-XXXX</span> job reference. Without it, your payment can't be matched to your invoice.</p>
              </li>
            </ul>
            {!pay?.bank_account_number && (
              <p className="mt-4 text-xs text-spark-slate italic">Bank details aren't published yet — they'll be on every invoice we email you, or just message us and we'll send them.</p>
            )}
          </div>
        </section>

        {/* The 4-step flow */}
        <section>
          <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold mb-6 border-b-2 border-spark-ink pb-2">Step by step</h2>
          <div className="space-y-4">
            <Step n={1} title="Receive your invoice">
              <p>When your scooter is <strong>ready for pickup</strong>, we send you one email with the ready-for-pickup notice and the invoice PDF attached. The invoice has the full breakdown, our bank details, and your job reference.</p>
            </Step>
            <Step n={2} title="Pay by bank transfer">
              <p>Log in to your banking app and pay the amount shown on the invoice using the account details above.</p>
              <p><strong>Use the SPK-XX-XXXX reference</strong> from your invoice. This is how we match your payment to your job — without it, we can't reconcile it.</p>
              <p className="text-xs italic">Cash on collection is also fine if you'd rather pay in person.</p>
            </Step>
            <Step n={3} title="Send a payment screenshot">
              <p>Once you've paid, send us a screenshot of the transfer so we can verify it before you arrive. Two easy ways:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Open your <Link to="/track" className="text-spark-teal hover:text-spark-teal-deep font-semibold underline">job tracker</Link> and use the "Send proof of payment" upload box.</li>
                <li>Or simply reply to the invoice email with the screenshot attached.</li>
              </ul>
              <p>Most bank transfers clear within minutes inside NZ, but a screenshot lets us release your scooter as soon as you arrive.</p>
            </Step>
            <Step n={4} title="Get your tax receipt">
              <p>As soon as we mark your invoice as paid, you'll automatically receive a <strong>tax receipt PDF</strong> by email. Keep it for your records — it doubles as proof of payment for warranty or insurance claims.</p>
              {pay?.gst_registered ? (
                <p>Receipts include our GST number ({pay.gst_number || "—"}) and the {gstPct}% GST breakdown, suitable for business expense claims.</p>
              ) : (
                <p className="text-xs italic">We're not currently GST-registered, so no GST is charged on top of our prices. Receipts are still itemised and dated for your records.</p>
              )}
            </Step>
          </div>
        </section>

        {/* Other ways to pay */}
        <section>
          <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold mb-6 border-b-2 border-spark-ink pb-2">Other ways to pay</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="surface-card p-5">
              <Wallet className="size-5 text-spark-teal mb-2" />
              <p className="font-display uppercase tracking-wider font-bold text-sm">Cash</p>
              <p className="text-xs text-spark-slate mt-1.5 leading-relaxed">Hand over at pickup. We'll print or email you a paid receipt on the spot.</p>
            </div>
            <div className="surface-card p-5">
              <CreditCard className="size-5 text-spark-teal mb-2" />
              <p className="font-display uppercase tracking-wider font-bold text-sm">Card / Online</p>
              <p className="text-xs text-spark-slate mt-1.5 leading-relaxed">Card payments go through our online <Link to="/shop" className="text-spark-teal hover:text-spark-teal-deep underline">shop checkout</Link>. For repair invoices we currently use bank transfer to keep prices low.</p>
            </div>
            <div className="surface-card p-5">
              <ShieldCheck className="size-5 text-spark-teal mb-2" />
              <p className="font-display uppercase tracking-wider font-bold text-sm">Approved invoice</p>
              <p className="text-xs text-spark-slate mt-1.5 leading-relaxed">Existing approved customers can pay within {pay?.payment_terms_days || 7} days. A $5/week holding fee applies after a 14-day grace period.</p>
            </div>
          </div>
        </section>

        {/* GST + receipts info */}
        <section data-testid="pay-gst-info">
          <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold mb-6 border-b-2 border-spark-ink pb-2">GST &amp; tax receipts</h2>
          <div className="surface-card p-6 sm:p-7 space-y-3 text-sm text-spark-slate leading-relaxed">
            {pay?.gst_registered ? (
              <>
                <p>Spark Scooter Repairs is registered for GST in New Zealand. All prices on the website are <strong>GST-inclusive</strong> ({gstPct}%) unless explicitly stated.</p>
                <p>Every invoice and receipt we issue clearly shows the GST component and our GST number ({pay.gst_number || "—"}). These are valid <strong>tax invoices</strong> under the Goods &amp; Services Tax Act 1985 — perfect for expense claims, business returns, or insurance.</p>
              </>
            ) : (
              <>
                <p>Spark Scooter Repairs is not currently registered for GST. Our prices are flat — what you see is what you pay, no GST added on top.</p>
                <p>We still email you a numbered, dated receipt for every paid invoice. Keep it for warranty claims, insurance, or your own records.</p>
              </>
            )}
            <p className="pt-2 flex items-center gap-2 text-xs italic"><Receipt className="size-3.5" /> Lost your receipt? Reply to any email from us and we'll resend it.</p>
          </div>
        </section>

        {/* Common questions */}
        <section>
          <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold mb-6 border-b-2 border-spark-ink pb-2">Common questions</h2>
          <dl className="space-y-4">
            {[
              {
                q: "What if I forget to use my SPK reference?",
                a: "No drama — just send us a screenshot of the transfer (or reply to the invoice email) and we'll match it manually. Always quicker if you include the reference though."
              },
              {
                q: "How long until you mark my payment as 'Paid'?",
                a: "Usually within an hour during business hours. Bank transfers within NZ are typically near-instant; once we see it land we mark you Paid and email the receipt automatically."
              },
              {
                q: "Can I pay in instalments?",
                a: "Special-order parts (batteries, motors, controllers) may require a deposit of 50% of the total quote before ordering, with the balance due on completion. Otherwise the invoice is due on completion. For larger jobs, talk to us before pickup and we'll work something out."
              },
              {
                q: "Do you accept Afterpay / Laybuy / Klarna?",
                a: "Not directly. The shop checkout uses Stripe; for repairs, bank transfer is preferred. If you need a payment plan, message us first."
              },
              {
                q: "Will I get a paper receipt?",
                a: "Receipts are emailed as a PDF — easier to keep, easier to find later. We can print one on pickup if you'd prefer paper."
              },
            ].map((item, i) => (
              <div key={i} className="surface-card p-5 sm:p-6">
                <dt className="font-display uppercase tracking-tight text-base font-bold mb-1.5">{item.q}</dt>
                <dd className="text-spark-slate text-sm leading-relaxed">{item.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* CTA */}
        <section className="bg-spark-teal-deep text-white rounded-md p-6 sm:p-10 space-y-4" data-testid="pay-cta">
          <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold">Ready to settle up?</h2>
          <p className="text-slate-300 max-w-xl">Find your job in the tracker, copy your bank details, transfer the amount on your invoice, and upload a screenshot. We'll handle the rest.</p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/track" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-spark-yellow text-spark-ink hover:bg-spark-yellow-dark font-display uppercase tracking-wider text-xs font-bold" data-testid="pay-cta-track">
              <Upload className="size-4" /> Open job tracker <ArrowRight className="size-3.5" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-white/20 hover:bg-white/10 font-display uppercase tracking-wider text-xs font-bold" data-testid="pay-cta-contact">
              <FileText className="size-4" /> Question about an invoice
            </Link>
          </div>
          {settings?.address_line && (
            <p className="text-xs text-slate-400 pt-3 border-t border-white/10 flex items-center gap-1.5"><MapPin className="size-3.5" /> Workshop: {settings.address_line}</p>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HowToPay;
